import React from 'react';
import { formatDuration } from '../../../../utils/utils';
import { TARGET_AGE_MAP } from '../../../../utils/META/cat_109';
import PoweredByDQ from '../../../../assets/images/PoweredByDQ_Default.png';
import { tUi } from '../../../../utils/dq_lc';

const TeachingPlanHtmlDoc = React.forwardRef(({ 
    programData, 
    programAudience, 
    programDuration, 
    modules = [], 
    isEducatorView, 
    selectedLanguage,
    uiLocale
}, ref) => {
    const totalModules = modules.length;
    const totalUnits = modules.reduce((acc, m) => acc + (m?.units?.length || 0), 0);
    const totalSessions = modules.reduce(
        (acc, m) => acc + (m?.units || []).reduce((a, u) => a + (u?.lessons?.length || 0), 0),
        0
    );

    const learnersAge = programAudience ? TARGET_AGE_MAP[programAudience]?.name || programAudience : '';
    const learnersText = learnersAge || tUi('teachingPlan.meta.notSpecified', uiLocale);

    const styles = `
        @media print {
            @page { size: A4; margin: 0; }
            body { margin: 0; padding: 0; }
            .pdf-container {
                padding: 60px 50px 50px 50px;
                -webkit-box-decoration-break: clone;
                box-decoration-break: clone;
            }
        }

        .pdf-container {
            font-family: 'Noto Sans KR', 'Noto Sans JP', 'Noto Sans Thai', Arial, sans-serif;
            font-size: 10pt;
            line-height: 1.5;
            color: #000;
            background: white;
            max-width: 800px;
            margin: 0 auto;
            padding: 80px 70px 72px 54px;
        }

        .header {
            text-align: right;
            margin-bottom: 0;
        }

        .header img {
            height: 45px;
            margin-left: 6px;
            margin-top: 0;
            margin-bottom: 20px;
        }

        .title {
            font-size: 18pt;
            font-weight: bold;
            text-align: center;
            margin-top: 10px;
            margin-bottom: 4px;
        }

        .subtitle {
            font-size: 16pt;
            font-weight: bold;
            text-align: center;
            margin-top: 8px;
            margin-bottom: 18px;
        }

        .meta-section {
            margin-top: 10px;
            margin-bottom: 8px;
        }

        .meta-line {
            font-size: 12pt;
            text-align: left;
            margin-bottom: 2px;
        }

        .meta-label {
            font-weight: bold;
        }

        .section-title {
            font-size: 14pt;
            font-weight: bold;
            margin-top: 12px;
            page-break-after: avoid;
            margin-bottom: 4px;
        }

        .overview-text {
            font-size: 12pt;
        }

        .prep-list {
            margin-left: 20px;
            margin-bottom: 8px;
        }

        .prep-item {
            display: flex;
            margin-bottom: 4px;
        }

        .prep-bullet {
            margin-right: 8px;
            font-size: 16pt;
            font-weight: bold;
        }

        .module-divider {
            height: 0.8px;
            background-color: #000;
            width: 100%;
            margin: 10px auto;
            border-radius: 1px;
        }

        .unit-divider {
            height: 0.6px;
            background-color: #d9d9d9;
            width: 77%;
            margin: 12px auto;
            border-radius: 1px;
        }

        .module-block {
            margin-bottom: 12px;
        }

        .module-description {
            margin-bottom: 10px;
            font-size: 12pt;
        }

        .unit-block {
            margin-top: 8px;
            margin-left: 10px;
            padding-left: 8px;
        }

        .unit-title-band {
            border-radius: 4px;
            padding: 4px 6px;
            margin-bottom: 4px;
            display: inline-block;
            page-break-after: avoid;
        }

        .unit-title-text {
            font-size: 14pt;
            padding: 0 6px;
            font-weight: bold;
        }

        .unit-description {
            padding-left: 13px;
            font-size: 12pt;
        }

        .session-block {
            margin-top: 14px;
            margin-left: 26px;
            padding: 0 18px;
        }

        .session-header {
            font-size: 14pt;
            font-weight: bold;
            page-break-after: avoid;
        }

        .session-meta {
            margin-top: 2px;
            margin-bottom: 3px;
            font-size: 12pt;
        }

        .objectives-section {
            margin-top: 2px;
        }

        .objectives-title {
            font-size: 13pt;
            font-weight: bold;
            margin-bottom: 2px;
            page-break-after: avoid;
        }

        .objective-item {
            display: flex;
            margin-left: 20px;
            margin-bottom: 6px;
        }

        .objective-bullet {
            margin-right: 8px;
            font-size: 25pt;
            font-weight: bold;
        }

        .objective-text {
            font-size: 12pt;
            margin-left: 10px;
            margin-bottom: 3px;
        }

        .time-table {
            margin-top: 12px;
            margin-bottom: 8px;
            border: 1px solid #000;
            border-collapse: collapse;
            width: 100%;
        }

        .time-table-header {
            background-color: #f0f0f0;
            border: 1px solid #000;
        }

        .time-table-header-cell {
            padding: 6px;
            border-right: 1px solid #000;
            font-weight: bold;
            font-size: 10pt;
        }

        .time-table-header-cell:last-child {
            border-right: none;
        }

        .time-table-row {
            border: 1px solid #000;
            border-top: none;
        }

        .time-table-cell {
            padding: 6px;
            border-right: 1px solid #000;
            font-size: 9pt;
            vertical-align: top;
        }

        .time-table-cell:last-child {
            border-right: none;
        }

        .activity-item {
            font-size: 9pt;
            margin-bottom: 2px;
        }

        .duration {
            font-size: 9pt;
            color: #000;
        }

        .teacher-notes {
            margin-top: 16px;
        }

        .notes-item {
            display: flex;
            margin-bottom: 5px;
        }

        .notes-bullet {
            margin-right: 8px;
            font-size: 14pt;
            font-weight: bold;
        }

        .notes-text {
            font-size: 12pt;
            line-height: 1.4;
        }

        .footer {
            margin-top: 40px;
            padding-top: 20px;
            text-align: center;
            font-size: 8pt;
            color: #444;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
        }

        .circle-icon {
            width: 9px;
            height: 9px;
            border: 1px solid #444;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 9pt;
            margin: 0 2px;
        }
    `;

    // Helper function to generate time table data for a session
    const generateTimeTableData = (session) => {
        const sessionDuration = session.lesson_duration || 0;
        const learningObjectives = session.session_learning_objectives || [];
        const hasLOs = learningObjectives.length > 0;

        const hasPreAssessment = true;
        const hasPostAssessment = true;

        const segments = [];

        const numSLOs = learningObjectives.length;
        const hasAssessments = hasPreAssessment || hasPostAssessment;
        const assessmentCount = (hasPreAssessment ? 1 : 0) + (hasPostAssessment ? 1 : 0);

        let introPercentage, sloPercentage, reflectionPercentage, recapPercentage, assessmentPercentage;

        if (hasAssessments && hasLOs) {
            assessmentPercentage = 0.10;
            introPercentage = 0.08;
            const remainingPercentage = 1 - (assessmentCount * assessmentPercentage) - introPercentage - 0.12 - 0.10;
            sloPercentage = remainingPercentage / numSLOs;
            reflectionPercentage = 0.12;
            recapPercentage = 0.10;
        } else if (hasLOs) {
            introPercentage = 0.10;
            sloPercentage = 0.60 / numSLOs;
            reflectionPercentage = 0.15;
            recapPercentage = 0.15;
        } else {
            introPercentage = 0.15;
            reflectionPercentage = 0.20;
            recapPercentage = 0.15;
        }

        const hasPoll = (lo) => {
            return lo?.has_poll ||
                   lo?.includes_poll ||
                   lo?.activities?.some(a => a?.type === 'poll' || a?.activity_type === 'poll') ||
                   false;
        };

        // 1. Introduction
        segments.push({
            name: tUi('teachingPlan.segment.introduction', uiLocale),
            percentage: introPercentage,
            activities: [
                tUi('teachingPlan.segment.introduction.activity1', uiLocale),
                tUi('teachingPlan.segment.introduction.activity2', uiLocale)
            ],
            description: tUi('teachingPlan.segment.introduction.description', uiLocale)
        });

        // 2. Pre-Assessment
        if (hasPreAssessment) {
            segments.push({
                name: tUi('teachingPlan.segment.preAssessment', uiLocale),
                percentage: assessmentPercentage,
                activities: [
                    tUi('teachingPlan.segment.preAssessment.activity1', uiLocale),
                    tUi('teachingPlan.segment.preAssessment.activity2', uiLocale)
                ],
                description: tUi('teachingPlan.segment.preAssessment.description', uiLocale)
            });
        }

        // 3. SLO Segments
        if (hasLOs) {
            learningObjectives.forEach((lo, idx) => {
                const sloTitle = lo.slo_title || `${tUi('teachingPlan.segment.learningObjective', uiLocale)} ${idx + 1}`;
                const loDesc = lo.description_for_educator || lo.description_for_learner || '';

                const sloActivities = [
                    tUi('teachingPlan.segment.slo.activity1', uiLocale),
                    tUi('teachingPlan.segment.slo.activity2', uiLocale)
                ];
                if (hasPoll(lo)) {
                    sloActivities.push(tUi('teachingPlan.segment.slo.activity3', uiLocale));
                }
                sloActivities.push(tUi('teachingPlan.segment.slo.activity4', uiLocale));

                segments.push({
                    name: sloTitle,
                    percentage: sloPercentage,
                    activities: sloActivities,
                    description: loDesc || tUi('teachingPlan.segment.slo.description', uiLocale)
                });
            });
        } else {
            segments.push({
                name: tUi('teachingPlan.segment.mainContent', uiLocale),
                percentage: 0.50,
                activities: [
                    tUi('teachingPlan.segment.mainContent.activity1', uiLocale),
                    tUi('teachingPlan.segment.mainContent.activity2', uiLocale),
                    tUi('teachingPlan.segment.mainContent.activity3', uiLocale)
                ],
                description: tUi('teachingPlan.segment.mainContent.description', uiLocale)
            });
        }

        // 4. Reflection
        segments.push({
            name: tUi('teachingPlan.segment.reflection', uiLocale),
            percentage: reflectionPercentage,
            activities: [
                tUi('teachingPlan.segment.reflection.activity1', uiLocale),
                tUi('teachingPlan.segment.reflection.activity2', uiLocale),
                tUi('teachingPlan.segment.reflection.activity3', uiLocale)
            ],
            description: tUi('teachingPlan.segment.reflection.description', uiLocale)
        });

        // 5. Post-Assessment
        if (hasPostAssessment) {
            segments.push({
                name: tUi('teachingPlan.segment.postAssessment', uiLocale),
                percentage: assessmentPercentage,
                activities: [
                    tUi('teachingPlan.segment.postAssessment.activity1', uiLocale),
                    tUi('teachingPlan.segment.postAssessment.activity2', uiLocale)
                ],
                description: tUi('teachingPlan.segment.postAssessment.description', uiLocale)
            });
        }

        // 6. Recap
        segments.push({
            name: tUi('teachingPlan.segment.recap', uiLocale),
            percentage: recapPercentage,
            activities: [
                tUi('teachingPlan.segment.recap.activity1', uiLocale),
                tUi('teachingPlan.segment.recap.activity2', uiLocale),
                tUi('teachingPlan.segment.recap.activity3', uiLocale)
            ],
            description: tUi('teachingPlan.segment.recap.description', uiLocale)
        });

        // Calculate actual minutes
        const timeAllocations = segments.map(seg => Math.round(sessionDuration * seg.percentage));
        let totalAllocated = timeAllocations.reduce((sum, time) => sum + time, 0);

        // Adjust to match exact session duration
        if (totalAllocated !== sessionDuration && sessionDuration > 0) {
            const difference = sessionDuration - totalAllocated;
            const largestIdx = timeAllocations.indexOf(Math.max(...timeAllocations));
            timeAllocations[largestIdx] += difference;
        }

        return segments.map((seg, idx) => ({
            ...seg,
            estimatedMinutes: timeAllocations[idx]
        }));
    };

    return (
        <div ref={ref}>
            <style>{styles}</style>
            <div className="pdf-container">
                {/* Header */}
                <div className="header">
                    <img src={PoweredByDQ} alt="Powered by DQ" />
                </div>

                {/* Title */}
                <div className="title">{programData?.full_name}</div>
                <div className="subtitle">{tUi('teachingPlan.title.suffix', uiLocale)}</div>

                {/* Meta Information */}
                <div className="meta-section">
                    <div className="meta-line">
                        <span className="meta-label">{tUi('teachingPlan.meta.learners', uiLocale)} </span>{learnersText}
                    </div>
                    <div className="meta-line">
                        <span className="meta-label">{tUi('teachingPlan.meta.programStructure', uiLocale)} </span>
                        {totalModules} {tUi('teachingPlan.structure.modules', uiLocale)} / {totalUnits} {tUi('teachingPlan.structure.units', uiLocale)} / {totalSessions} {tUi('teachingPlan.structure.sessions', uiLocale)}
                    </div>
                    <div className="meta-line">
                        <span className="meta-label">{tUi('teachingPlan.meta.programLength', uiLocale)} </span>{formatDuration(programDuration, uiLocale)}
                    </div>
                </div>

                {/* Overview */}
                {programData?.description && (
                    <>
                        <div className="section-title">{tUi('teachingPlan.section.overview', uiLocale)}</div>
                        <div className="overview-text">{programData.description}</div>
                    </>
                )}

                {/* Preparing Your Class */}
                <div className="section-title">{tUi('teachingPlan.section.preparingClass', uiLocale)}</div>
                <div style={{ fontSize: '12pt', marginBottom: '4px' }}>{tUi('teachingPlan.prep.beforeBeginning', uiLocale)}</div>
                <div className="prep-list">
                    <div className="prep-item">
                        <span className="prep-bullet">•</span>
                        <span style={{ fontSize: '12pt' }}>
                            {tUi('teachingPlan.prep.internetAccess', uiLocale)
                                .split('{bold1}')[0]}
                            <strong>{tUi('teachingPlan.prep.internetAccessBold1', uiLocale)}</strong>
                            {tUi('teachingPlan.prep.internetAccess', uiLocale)
                                .split('{bold1}')[1]?.split('{bold2}')[0]}
                            <strong>{tUi('teachingPlan.prep.internetAccessBold2', uiLocale)}</strong>
                            {tUi('teachingPlan.prep.internetAccess', uiLocale)
                                .split('{bold2}')[1]?.replace('}', '')}
                        </span>
                    </div>
                    <div className="prep-item">
                        <span className="prep-bullet">•</span>
                        <span style={{ fontSize: '12pt' }}>
                            {tUi('teachingPlan.prep.studentAccounts', uiLocale)
                                .split('{link}')[0]}
                            <a href="https://www.dqforall.com" style={{ color: '#0066cc', textDecoration: 'underline' }}>
                                {tUi('teachingPlan.prep.studentAccountsLink', uiLocale)}
                            </a>
                            {tUi('teachingPlan.prep.studentAccounts', uiLocale)
                                .split('{link}')[1]?.replace('}', '')}
                        </span>
                    </div>
                    <div className="prep-item">
                        <span className="prep-bullet">•</span>
                        <span style={{ fontSize: '12pt' }}>
                            {tUi('teachingPlan.prep.websiteBlocked', uiLocale)
                                .split('{bold}')[0]}
                            <strong>{tUi('teachingPlan.prep.websiteBlockedBold', uiLocale)}</strong>
                            {tUi('teachingPlan.prep.websiteBlocked', uiLocale)
                                .split('{bold}')[1]?.replace('}', '')}
                        </span>
                    </div>
                    <div className="prep-item">
                        <span className="prep-bullet">•</span>
                        <span style={{ fontSize: '12pt' }}>
                            {tUi('teachingPlan.prep.headphones', uiLocale)
                                .split('{bold}')[0]}
                            <strong>{tUi('teachingPlan.prep.headphonesBold', uiLocale)}</strong>
                            {tUi('teachingPlan.prep.headphones', uiLocale)
                                .split('{bold}')[1]?.replace('}', '')}
                        </span>
                    </div>
                </div>

                {/* Modules / Units / Sessions */}
                {modules.map((mod, mIdx) => {
                    const moduleDescription = isEducatorView ? mod.module_educator_description : mod.module_learner_description;

                    return (
                        <div key={mIdx} className="module-block">
                            <div className="module-divider"></div>
                            <div className="section-title">
                                <span>{tUi('teachingPlan.module.label', uiLocale)} {mIdx + 1}: {mod.module_name} </span>
                                <span className="duration">({formatDuration(mod.module_duration, uiLocale)})</span>
                            </div>
                            {moduleDescription && (
                                <div className="module-description">{moduleDescription}</div>
                            )}

                            {/* Units */}
                            {(mod.units || []).map((u, uIdx) => {
                                const unitDescription = isEducatorView ? u.unit_educator_description : u.unit_learner_description;

                                return (
                                    <div key={uIdx} className="unit-block">
                                        <div className="unit-divider"></div>
                                        <div className="unit-title-band">
                                            <span className="unit-title-text">
                                                {tUi('teachingPlan.unit.label', uiLocale)} {uIdx + 1}: {u.unit_title} <span className="duration">({formatDuration(u.unit_duration, uiLocale)})</span>
                                            </span>
                                        </div>
                                        {unitDescription && (
                                            <div className="unit-description">{unitDescription}</div>
                                        )}

                                        {/* Sessions */}
                                        {(u.lessons || []).map((s, sIdx) => {
                                            const objectiveItems = (s.session_learning_objectives || [])
                                                .map((o) => o.description_for_educator || o.description_for_learner || o.description)
                                                .filter(Boolean);
                                            const sessionDescription = isEducatorView ? s.lesson_educator_description : s.lesson_learner_description;
                                            const timeTableRows = generateTimeTableData(s);

                                            return (
                                                <div key={sIdx} className="session-block">
                                                    <div className="session-header">
                                                        {tUi('teachingPlan.session.label', uiLocale)} {sIdx + 1}: {s.lesson_title || s.session_title || s.title || tUi('teachingPlan.session.untitled', uiLocale)} <span className="duration">({formatDuration(s.lesson_duration || s.session_duration || s.duration || 0, uiLocale)})</span>
                                                    </div>

                                                    {sessionDescription && (
                                                        <div className="session-meta">{sessionDescription}</div>
                                                    )}

                                                    {objectiveItems.length > 0 && (
                                                        <div className="objectives-section">
                                                            <div className="objectives-title">{tUi('teachingPlan.session.learningObjectives', uiLocale)}</div>
                                                            {objectiveItems.map((line, idx) => (
                                                                <div key={idx} className="objective-item">
                                                                    <span className="objective-bullet">•</span>
                                                                    <span className="objective-text">{line}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Estimated Time Table */}
                                                    <table className="time-table">
                                                        <thead>
                                                            <tr className="time-table-header">
                                                                <th className="time-table-header-cell" style={{ width: '20%' }}>{tUi('teachingPlan.table.estimatedTime', uiLocale)}</th>
                                                                <th className="time-table-header-cell" style={{ width: '20%' }}>{tUi('teachingPlan.table.segment', uiLocale)}</th>
                                                                <th className="time-table-header-cell" style={{ width: '25%' }}>{tUi('teachingPlan.table.activities', uiLocale)}</th>
                                                                <th className="time-table-header-cell" style={{ width: '35%' }}>{tUi('teachingPlan.table.description', uiLocale)}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {timeTableRows.map((row, rowIdx) => {
                                                                const timeDisplay = row.estimatedMinutes > 0 ? `${row.estimatedMinutes} ${tUi('teachingPlan.table.minutes', uiLocale)}` : '-';
                                                                return (
                                                                    <tr key={rowIdx} className="time-table-row">
                                                                        <td className="time-table-cell">{timeDisplay}</td>
                                                                        <td className="time-table-cell">{row.name}</td>
                                                                        <td className="time-table-cell">
                                                                            {row.activities.map((activity, actIdx) => (
                                                                                <div key={actIdx} className="activity-item">• {activity}</div>
                                                                            ))}
                                                                        </td>
                                                                        <td className="time-table-cell">{row.description}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}

                {/* Teacher Notes */}
                <div className="teacher-notes">
                    <div className="section-title">{tUi('teachingPlan.section.teacherNotes', uiLocale)}</div>
                    <div className="notes-item">
                        <span className="notes-bullet">•</span>
                        <span className="notes-text">{tUi('teachingPlan.notes.note1', uiLocale)}</span>
                    </div>
                    <div className="notes-item">
                        <span className="notes-bullet">•</span>
                        <span className="notes-text">{tUi('teachingPlan.notes.note2', uiLocale)}</span>
                    </div>
                    <div className="notes-item">
                        <span className="notes-bullet">•</span>
                        <span className="notes-text">
                            {tUi('teachingPlan.notes.note3', uiLocale).split('{bold}')[0]}
                            <strong>{tUi('teachingPlan.notes.note3Bold', uiLocale)}</strong>
                            {tUi('teachingPlan.notes.note3', uiLocale).split('{bold}')[1]?.replace('}', '')}
                        </span>
                    </div>
                </div>

                {/* Footer */}
                <div className="footer">
                    <span className="circle-icon">C</span>
                    <span> {new Date().getFullYear()} {tUi('teachingPlan.footer.copyright', uiLocale)}</span>
                    <span className="circle-icon">R</span>
                    <span> {tUi('teachingPlan.footer.trademark', uiLocale)}</span>
                </div>
            </div>
        </div>
    );
});

TeachingPlanHtmlDoc.displayName = 'TeachingPlanHtmlDoc';

export default TeachingPlanHtmlDoc;
